/**
 * Sampling tab (chooser) — Access `frmQualitySamplingSubForm2` + inner `frmQualityQrySampling2`.
 *
 * Behavior (Change_Info / Form_Load):
 *  - cp_code combo (RowSource QualityQrySamplingChoose_CP) → on change rebuilds the
 *    tlc_contract_ref combo (QualityQrySamplingChoose_S WHERE cp_code=…) and selects
 *    its first item.
 *  - Both combos drive the read-only detail list + the Sent/Count/% summary panel,
 *    computed over `tlc_factory.view_factory_sampling3` (is_status=0).
 *  - NO New/Edit/Delete on this tab (chooser-only per Access evidence).
 */
import { Autocomplete, Box, CircularProgress, Stack, TablePagination, TextField, Typography } from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import { type CSSProperties } from 'react'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import { TableVirtuoso } from 'react-virtuoso'

import BrandState from '@/components/BrandState'
import ResizeHandle from '@/components/ResizeHandle'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import {
  DataframeBody,
  type ColumnFilterAnchorPosition,
  type ColumnFilterKind,
  ColumnFilterIcon,
  ColumnFilterPopover,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
} from '@/components/dataList'
import { COMBOBOX_FILTER_COMPACT, TABLE_PAGINATION_COMPACT } from '@/theme/responsive'
import { formatDate } from '@/utils/date'
import { formatNumber } from '@/utils/money'
import {
  getTableSortSuffix,
  sortTableRows,
  toggleTableSort,
  type TableSortColumn,
  type TableSortState,
} from '@/utils/tableSort'
import {
  useGetSamplingChooseQuery,
  useGetSamplingContractRefsQuery,
  useGetSamplingCpCodesQuery,
  type SamplingChooseRow,
} from '@/store/api/qualityApi'
import { usePreference } from '@/hooks/usePreference'

/** Per-user chooser selection persisted via user_preferences (scope quality / page_filter, sub_key 'sampling'). */
interface QualitySamplingChooser {
  cpCode: string | null
  contractRef: string | null
}

type ColKind = 'text' | 'date' | 'number'
interface ColumnDef extends TableSortColumn<string> {
  key: keyof SamplingChooseRow
  label: string
  kind: ColKind
  align?: 'left' | 'right'
  /** Default width (px) — user resize overrides. */
  width?: number
  /** Long text → wrap + row auto-grows (gold list `/sales/contract/list`). */
  wrap?: boolean
}

// Inner list columns — verbatim from frmQualityQrySampling2 (datasheet order + captions).
// NB: license_cert_no's Access label literally reads "Weight/ cont (MT)" (a copy-paste
// bug in the original form); kept verbatim per the convert-as-is rule.
const COLUMNS: ColumnDef[] = [
  { key: 'psDate', label: 'PS date', kind: 'date' },
  { key: 'clientLotId', label: 'Lot Number', kind: 'text' },
  { key: 'lotSizeMt', label: 'Lot size (MT)', kind: 'number', align: 'right' },
  { key: 'weightPerContMt', label: 'Weight/ cont (MT)', kind: 'number', align: 'right' },
  { key: 'licenseCertNo', label: 'Weight/ cont (MT)', kind: 'text' },
  { key: 'result', label: 'Status', kind: 'text' },
  { key: 'rejectReason', label: 'Rejected reason', kind: 'text', wrap: true, width: 240 },
]

const FROZEN_KEY = 'psDate'

// Combobox sizing — shared `COMBOBOX_FILTER_COMPACT` (same as BatchReportPage PO
// selector). `transform: 'none'` cancels the xs scale (we use a real width instead,
// input font stays 16px on xs → no iOS Safari auto-zoom). sx-array keeps the
// constant intact without touching input fontSize.
const comboSx: SxProps<Theme> = [
  COMBOBOX_FILTER_COMPACT as Exclude<SxProps<Theme>, readonly unknown[]>,
  { transform: 'none', width: '100%' },
]

const num = (v: number | null | undefined) => (v == null ? '' : formatNumber(v, 2))
const pct = (v: number) => `${formatNumber(v * 100, 1)}%`

// Readonly snapshot field style (convert-form §Readonly field): gray bg + secondary
// bold text so the additional-info block reads as display-only, not editable.
const readonlyFieldSx = {
  '& .MuiInputBase-root': {
    bgcolor: (theme: Theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5'),
  },
  '& .MuiInputBase-input': {
    color: (theme: Theme) => theme.palette.text.secondary,
    fontWeight: 600,
  },
}
const numericInputProps = { style: { textAlign: 'right' as const } }

function cellValue(row: SamplingChooseRow, col: ColumnDef): string {
  const raw = row[col.key]
  if (col.kind === 'date') return raw ? formatDate(String(raw)) : ''
  if (col.kind === 'number') return num(raw as number | null)
  return raw == null ? '' : String(raw)
}

export default function SamplingChooserView() {
  const [cpCode, setCpCode] = useState<string | null>(null)
  const [contractRef, setContractRef] = useState<string | null>(null)
  const [cpTouched, setCpTouched] = useState(false)
  const [refTouched, setRefTouched] = useState(false)

  // Per-user chooser persistence (scope quality / page_filter, sub_key 'sampling').
  const chooserPref = usePreference<QualitySamplingChooser>('quality', 'page_filter', 'sampling')
  // `prefReady` (state, not ref) gates the auto-select-first effects so they DON'T run
  // until the saved selection has been read from DB — otherwise auto-select picks the
  // first option before hydration and the user's saved choice never shows on reload.
  const [prefReady, setPrefReady] = useState(false)
  // Stable ref to patch — usePreference returns a fresh object each render, so depending
  // on `chooserPref` in the debounce effect would reset the 400ms timer every render
  // (frequent due to queries) → patch never fires → nothing persists.
  const patchRef = useRef(chooserPref.patch)
  patchRef.current = chooserPref.patch
  const prefLoading = chooserPref.isLoading
  const prefValue = chooserPref.value
  const prefUser = chooserPref.userOverride

  // Hydrate once from saved selection — overrides the Form_Load first-option defaults.
  // Dùng `userOverride` (key tồn tại = user đã chủ động chọn HOẶC xoá): nếu user từng
  // xoá Contract Ref (lưu null) thì giữ trạng thái rỗng, KHÔNG để auto-select điền lại.
  useEffect(() => {
    if (prefLoading || prefReady) return
    if ('cpCode' in prefUser) {
      if (prefValue.cpCode) setCpCode(prefValue.cpCode)
      setCpTouched(true)
    }
    if ('contractRef' in prefUser) {
      if (prefValue.contractRef) setContractRef(prefValue.contractRef)
      setRefTouched(true)
    }
    setPrefReady(true)
  }, [prefLoading, prefValue, prefUser, prefReady])

  // Persist whenever the selection changes (debounced). No tag invalidation needed —
  // cpCode/contractRef are FE query args, so RTK refetches on change automatically.
  useEffect(() => {
    if (!prefReady) return
    const t = setTimeout(() => {
      void patchRef.current({ cpCode, contractRef }).catch(() => {})
    }, 400)
    return () => clearTimeout(t)
  }, [cpCode, contractRef, prefReady])

  const cpCodesQ = useGetSamplingCpCodesQuery()
  const refsQ = useGetSamplingContractRefsQuery(cpCode ? { cpCode } : undefined)
  const chooseQ = useGetSamplingChooseQuery({
    cpCode: cpCode ?? undefined,
    contractRef: contractRef ?? undefined,
  })

  // Form_Load: default cp_code = first CP option — chỉ sau khi đã đọc preference (prefReady).
  useEffect(() => {
    if (!prefReady) return
    if (!cpTouched && cpCode == null && cpCodesQ.data && cpCodesQ.data.length > 0) {
      setCpCode(cpCodesQ.data[0].value)
    }
  }, [prefReady, cpTouched, cpCode, cpCodesQ.data])

  // cp_code change / Form_Load: select first contract ref of the (re)built list.
  useEffect(() => {
    if (!prefReady || refTouched) return
    if (refsQ.data && refsQ.data.length > 0) {
      const first = refsQ.data[0].value
      if (first !== contractRef) setContractRef(first)
    }
  }, [prefReady, refTouched, refsQ.data, contractRef])

  const handleCpChange = (value: string | null) => {
    setCpTouched(true)
    setRefTouched(false) // allow auto-select of first ref for the new cp
    setCpCode(value)
    setContractRef(null)
  }

  const handleRefChange = (value: string | null) => {
    setRefTouched(true)
    setContractRef(value)
  }

  const summary = chooseQ.data?.summary
  const baseRows = useMemo<SamplingChooseRow[]>(() => chooseQ.data?.rows ?? [], [chooseQ.data])

  // ── list filtering / sorting ──────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<string> | null>(null)
  const { getWidth, startResize } = useColumnWidths('quality.sampling-chooser')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{ key: string; position: ColumnFilterAnchorPosition } | null>(null)

  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (r) =>
          rowMatchesSearch(r, searchTerm, COLUMNS as never) &&
          rowMatchesAllColumnFilters(r, columnFilters.filters),
      ),
    [baseRows, searchTerm, columnFilters.filters],
  )
  const sortedRows = useMemo(
    () => sortTableRows(filteredRows, sortState, COLUMNS),
    [filteredRows, sortState],
  )
  const rows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])

  const popoverColumn = colPopover ? COLUMNS.find((c) => c.key === colPopover.key) ?? null : null
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover ? distinctColumnValues(baseRows, colPopover.key) : undefined,
    [popoverKind, colPopover, baseRows],
  )

  const toggleSort = (key: string) => {
    setSortState((cur) => toggleTableSort(cur, key))
    setPage(0)
  }
  // Per-column totals for the footer Total row (number columns only).
  const columnTotals = useMemo(() => {
    const totals = new Map<string, number | null>()
    for (const col of COLUMNS) {
      if (col.kind !== 'number') continue
      let sum = 0
      let count = 0
      for (const r of filteredRows) {
        const v = r[col.key as keyof SamplingChooseRow]
        if (typeof v === 'number' && Number.isFinite(v)) {
          sum += v
          count += 1
        }
      }
      totals.set(col.key, count > 0 ? sum : null)
    }
    return totals
  }, [filteredRows])

  // Inject saved value into options so the combo always displays it even if the loaded
  // option list doesn't contain it yet (memory feedback_autocomplete_inject_current_value).
  const cpOptions = useMemo(() => {
    const opts = cpCodesQ.data ?? []
    if (cpCode && !opts.some((o) => o.value === cpCode)) return [{ value: cpCode, label: cpCode }, ...opts]
    return opts
  }, [cpCodesQ.data, cpCode])
  const cpSel = cpOptions.find((o) => o.value === cpCode) ?? null
  const refOptions = useMemo(() => {
    const opts = refsQ.data ?? []
    if (contractRef && !opts.some((o) => o.value === contractRef)) return [{ value: contractRef, label: contractRef }, ...opts]
    return opts
  }, [refsQ.data, contractRef])
  const refSel = refOptions.find((o) => o.value === contractRef) ?? null

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* ── Chooser combos — căn với 2 cột con của NỬA TRÁI panel:
            CP Code thẳng "Buyer's Ref", Contract Ref thẳng "Package".
            Top-level grid 1fr 1fr (trùng panel) → combo nằm trong nửa trái;
            nửa trái lại chia 2 cột con (columnGap 1.25) trùng left-half panel. ── */}
      <Box
        sx={{
          mb: 1,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          columnGap: 3,
        }}
      >
        <Box
          sx={{
            gridColumn: { md: 1 },
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            columnGap: 1.25,
            rowGap: 1,
          }}
        >
          <Autocomplete
            size="small"
            options={cpOptions}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            loading={cpCodesQ.isFetching}
            value={cpSel}
            onChange={(_, v) => handleCpChange(v?.value ?? null)}
            sx={comboSx}
            renderInput={(p) => <TextField {...p} label="CP Code" />}
          />
          <Autocomplete
            size="small"
            options={refOptions}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            loading={refsQ.isFetching}
            value={refSel}
            onChange={(_, v) => handleRefChange(v?.value ?? null)}
            sx={comboSx}
            renderInput={(p) => <TextField {...p} label="Contract Ref" />}
          />
        </Box>
      </Box>

      {/* ── Summary panel ── */}
      <SummaryPanel summary={summary} loading={chooseQ.isFetching} />

      {/* ── Detail list ── */}
      <Box className="app-dataframe" sx={{ flex: 1, minHeight: 0, height: 'auto', display: 'flex', flexDirection: 'column' }}>
        <FilterBar
          hasActiveFilters={!!searchTerm.trim() || columnFilters.hasAny}
          onClearAll={() => {
            setSearchTerm('')
            columnFilters.clearAll()
            setPage(0)
          }}
          rightSlot={
            <Stack direction="row" alignItems="center" spacing={0.4}>
              {chooseQ.isFetching && <CircularProgress size={15} />}
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', whiteSpace: 'nowrap' }}>
                Total: {filteredRows.length}
              </Typography>
              <TablePagination
                component="div"
                count={filteredRows.length}
                page={rowsPerPage === -1 ? 0 : page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(Number(e.target.value))
                  setPage(0)
                }}
                rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
                labelRowsPerPage="Rows"
                sx={TABLE_PAGINATION_COMPACT}
              />
            </Stack>
          }
        >
          <FilterButton
            label="Search"
            active={!!searchTerm.trim()}
            onClick={(e) => filterPopover.open('search', e)}
          />
        </FilterBar>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={filterPopover.close}
          maxWidth={320}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => {
                setSearchTerm(v)
                setPage(0)
              }}
              placeholder="Lot number, status, reason..."
              label="Search Sampling"
              onClear={filterPopover.close}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={() => setColPopover(null)}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[colPopover.key] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(colPopover.key, v)
            setPage(0)
          }}
          options={popoverOptions}
        />

        <DataframeBody loading={chooseQ.isLoading} fetching={chooseQ.isFetching} sx={{ minHeight: { xs: 240, sm: 280 } }}>
          {chooseQ.isError ? (
            <BrandState
              tone="error"
              title="Could not load sampling list"
              description="Please refresh the page or contact the system administrator if the issue continues."
            />
          ) : chooseQ.isLoading ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState title="No sampling records found" description="Try adjusting the chooser or filters above." />
          ) : (
            <TableVirtuoso
              style={{ height: '100%' }}
              data={rows}
              fixedHeaderContent={() => (
                <TableRow>
                  {COLUMNS.map((col) => {
                    const isFrozen = col.key === FROZEN_KEY
                    const w = getWidth(col.key) ?? col.width
                    const headerStyle: CSSProperties = isFrozen
                      ? { position: 'sticky', top: 0, left: 0, zIndex: 100 }
                      : {}
                    if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(col.key)}
                        style={Object.keys(headerStyle).length ? headerStyle : undefined}
                        sx={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'text', ...(isFrozen ? {} : { position: 'relative' }) }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(col.key)}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(col.key, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={() => (
                <TableRow>
                  {COLUMNS.map((col, index) => {
                    const isFrozen = col.key === FROZEN_KEY
                    const total = columnTotals.get(col.key)
                    const val = total == null ? '' : num(total)
                    const w = getWidth(col.key) ?? col.width
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          py: 0.5,
                          bgcolor: 'background.paper',
                          borderTop: (t) => `1px solid ${t.palette.divider}`,
                        }}
                      >
                        {index === 0 ? 'Total' : val}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_, row: SamplingChooseRow) =>
                COLUMNS.map((col) => {
                  const isFrozen = col.key === FROZEN_KEY
                  const w = getWidth(col.key) ?? col.width
                  const widthStyle = w ? { width: w, minWidth: w, maxWidth: w } : undefined
                  // WRAP: cột flag `wrap` HOẶC user đã resize → chữ dài xuống dòng.
                  const wrap = col.wrap || getWidth(col.key) != null
                  if (wrap) {
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={widthStyle}
                        sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                      >
                        <Box component="span" className="app-table-cell-wrap">
                          {cellValue(row, col) || '-'}
                        </Box>
                      </TableCell>
                    )
                  }
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      className={isFrozen ? 'app-sticky-col' : undefined}
                      style={widthStyle}
                      sx={{ whiteSpace: 'nowrap', py: 0.5, verticalAlign: 'top' }}
                    >
                      {cellValue(row, col) || '-'}
                    </TableCell>
                  )
                })
              }
              components={{
                Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ width: '100%' }} />,
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => <TableRow {...props} hover />,
              }}
            />
          )}
        </DataframeBody>

      </Box>
    </Box>
  )
}

// ── Summary panel (header textboxes of frmQualitySamplingSubForm2) ────────────
function SummaryPanel({
  summary,
  loading,
}: {
  summary?: import('@/store/api/qualityApi').SamplingChooseSummary
  loading: boolean
}) {
  // ── Panel = 2 equal halves (5/10 each). Both halves share the SAME 6 row tracks
  // via CSS subgrid so descriptive rows and matrix rows stay on the same baselines.
  // Mirrors frmQualitySamplingSubForm2 (Access Left/Top): left = 2-col label:value,
  // right = Status matrix (Total→row2=Buyer's Ref, Accept/Reject/Waiting→rows 3–5).
  const matrix: Array<[string, import('@/store/api/qualityApi').SamplingSummaryMetric | undefined]> = [
    ['Total lot size', summary?.total],
    ['Accept', summary?.accept],
    ['Reject', summary?.reject],
    ['Waiting', summary?.waiting],
  ]

  // Readonly snapshot field (convert-form style): bg xám, label shrink, số căn phải.
  // Client Sample Ref multiline cao 4 dòng (= Shipping month+Quantity+Grade+Certification)
  // → value dài scroll trong khung, không kéo cao panel.
  const roField = (
    label: string,
    value: string | null | undefined,
    opts: { col: number; row?: number; rowSpan?: string; numeric?: boolean; multiline?: boolean },
  ) => (
    <TextField
      key={`ro-${label}`}
      label={label}
      size="small"
      fullWidth
      value={value ?? ''}
      multiline={opts.multiline}
      InputProps={{
        readOnly: true,
        // Multiline (Client Sample Ref): input wrapper + textarea fill toàn bộ
        // chiều cao ô grid (4 dòng), nội dung dài thì scroll trong khung.
        ...(opts.multiline
          ? { sx: { height: '100%', alignItems: 'stretch', '& textarea': { height: '100% !important', overflow: 'auto !important' } } }
          : {}),
      }}
      InputLabelProps={{ shrink: true }}
      inputProps={opts.numeric ? numericInputProps : undefined}
      sx={{
        gridColumn: { xs: 1, sm: opts.col },
        gridRow: { md: opts.rowSpan ?? opts.row },
        ...(opts.multiline ? { alignSelf: 'stretch', height: '100%' } : {}),
        ...readonlyFieldSx,
        '& .MuiInputBase-input': {
          ...(readonlyFieldSx['& .MuiInputBase-input'] as object),
          fontSize: '0.78rem',
        },
      }}
    />
  )

  return (
    <Box
      sx={{
        mb: 1,
        p: 1.25,
        borderRadius: 1.5,
        border: (t) => `1px solid ${t.palette.divider}`,
        bgcolor: 'background.paper',
        opacity: loading ? 0.6 : 1,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gridTemplateRows: { md: 'repeat(6, auto)' },
        columnGap: 3,
        rowGap: { xs: 1.5, md: 0.5 },
      }}
    >
      {/* Left half (5/10) — additional-info, convert-form readonly-field style:
          mỗi field 1 TextField readOnly (bg xám, label shrink), số căn phải Standard
          2dp, ngày DD-MMM-YYYY. 2-col grid; Package trên, Client Sample Ref dưới cao
          bằng 4 dòng cột trái (Shipping month + Quantity + Grade + Certification). */}
      <Box
        sx={{
          gridColumn: { md: 1 },
          gridRow: { md: '1 / 7' },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          gridTemplateRows: { md: 'subgrid' },
          columnGap: 1.25,
          rowGap: 0.75,
          alignItems: 'start',
        }}
      >
        {roField("Buyer's Ref", summary?.buyersRef, { col: 1, row: 1 })}
        {roField('Shipping month', summary?.deliveryTo ? formatDate(summary.deliveryTo) : '', { col: 1, row: 2 })}
        {roField('Quantity', num(summary?.quantity), { col: 1, row: 3, numeric: true })}
        {roField('Grade', summary?.quality, { col: 1, row: 4 })}
        {roField('Certification', summary?.certification, { col: 1, row: 5 })}
        {roField('Package', summary?.package, { col: 2, row: 1 })}
        {roField('Client Sample Ref', summary?.clientSampleRef, { col: 2, rowSpan: '2 / 6', multiline: true })}
      </Box>

      {/* Right half (5/10) — Status matrix, subgrid rows so it lines up with the left */}
      <Box
        sx={{
          gridColumn: { md: 2 },
          gridRow: { md: '1 / 7' },
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.4fr) repeat(3, minmax(0, 1fr))',
          gridTemplateRows: { md: 'subgrid' },
          border: (t) => `1px solid ${t.palette.divider}`,
          borderRadius: 1,
          bgcolor: 'background.paper',
          overflow: 'hidden',
          alignSelf: 'start',
          // Stacked layout (xs/sm): cap width + left-align so the table doesn't
          // stretch full-width on tablet; md+ fills its 5/10 half.
          justifySelf: 'start',
          maxWidth: { xs: '100%', sm: 460, md: 'none' },
          width: { md: '100%' },
        }}
      >
        {(['Status', 'Lot size', 'Count', '%'] as const).map((h, i) => (
          <Box
            key={h}
            sx={{
              gridRow: 1,
              gridColumn: i + 1,
              px: 1,
              py: 0.4,
              bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
              borderBottom: (t) => `1px solid ${t.palette.divider}`,
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.66rem', display: 'block', textAlign: i === 0 ? 'left' : 'right' }}>
              {h}
            </Typography>
          </Box>
        ))}
        {matrix.map(([label, m], i) => {
          const row = i + 2
          const last = i === matrix.length - 1
          const bb = last ? 'none' : (t: any) => `1px solid ${t.palette.divider}`
          const txt = (node: ReactNode, right: boolean) => (
            <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', width: '100%', textAlign: right ? 'right' : 'left', fontWeight: right ? 400 : 600 }}>
              {node}
            </Typography>
          )
          return [
            <Box key={`${row}-1`} sx={{ gridRow: row, gridColumn: 1, px: 1, py: 0.35, borderBottom: bb }}>{txt(label, false)}</Box>,
            <Box key={`${row}-2`} sx={{ gridRow: row, gridColumn: 2, px: 1, py: 0.35, borderBottom: bb }}>{txt(m ? num(m.value) : '-', true)}</Box>,
            <Box key={`${row}-3`} sx={{ gridRow: row, gridColumn: 3, px: 1, py: 0.35, borderBottom: bb }}>{txt(m ? m.count : '-', true)}</Box>,
            <Box key={`${row}-4`} sx={{ gridRow: row, gridColumn: 4, px: 1, py: 0.35, borderBottom: bb }}>{txt(m && m.value ? pct(m.per) : '-', true)}</Box>,
          ]
        })}
      </Box>
    </Box>
  )
}
